import { distinctUntilChanged, filter, fromEvent, map, merge, Observable, shareReplay, Subject } from 'rxjs';
import { denormalizeKey, normalizeKey } from './storage-helper.util';

export type StorageConfig = {
  prefix?: string | undefined;
  suffix?: string | undefined;
  /** delimiter used for prefix and suffix */
  delimiter?: string | undefined;
  /** Compatibility to ngx-webstorage. Several tests especially cypress tests are only working in insensitive mode (which is default in both this implementation and ngx-webstorage) */
  caseSensitive?: boolean;
  /** The underlying Storage implementation like window.localStorage / window.sessionStorage */
  storage?: Storage;
  /** If true, null values will be removed from storage like undefined values. Default is false. */
  removeNull?: boolean;
};
export type OverrideStorageConfig = Partial<Omit<StorageConfig, 'storage'>>;

const DEFAULT_CONFIG: StorageConfig = {
  prefix: undefined,
  suffix: undefined,
  delimiter: '_',
  storage: sessionStorage,
  removeNull: false,
  caseSensitive: false,
};

export class StorageService implements Storage {
  static readonly storages: Map<Storage, StorageService> = new Map();

  private config: StorageConfig;
  private storage: Storage;

  /** React to internal and external changes to the web storage */
  internalStorageChanges$ = new Subject<Pick<StorageEvent, 'key' | 'newValue' | 'oldValue' | 'storageArea'>>();
  /** Window storage event only emits if external changes in the storage area are happening (another tab for example) */
  externelStorageChanges$ = fromEvent<Pick<StorageEvent, 'key' | 'newValue' | 'oldValue' | 'storageArea'>>(window, 'storage').pipe(
    filter(e => e.storageArea === this.storage),
  );
  /** Combines internal and external changes to the storage */
  storageChanges$ = merge(this.internalStorageChanges$, this.externelStorageChanges$);

  constructor(config: StorageConfig = DEFAULT_CONFIG) {
    this.config = config;
    this.storage = config.storage ?? sessionStorage;
  }

  /** For compatibility with ngx-webstorage */
  observe<T = unknown>(key: string): Observable<T> {
    const denormalizedKey = key;
    key = normalizeKey(key, this.config.delimiter, this.config.prefix, this.config.suffix, this.config.caseSensitive);
    return this.storageChanges$.pipe(
      filter(e => e.key === key || e.key === null),
      map(e => this.getItem<T>(denormalizedKey)),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  withConfig(overrideConfig: OverrideStorageConfig): StorageService {
    const newConfig: StorageConfig = {
      ...this.config,
      ...overrideConfig,
    };
    return new StorageService(newConfig);
  }

  /** For compatibility with ngx-webstorage (alias for getItem) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  retrieve<T = any>(key: string): T | null {
    return this.getItem<T>(key);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItem<T = any>(key: string): T | null {
    key = normalizeKey(key, this.config.delimiter, this.config.prefix, this.config.suffix, this.config.caseSensitive);
    const value = this.storage.getItem(key);
    if (value === null) {
      return null;
    }
    try {
      return JSON.parse(value) as T;
    } catch (e) {
      console.error(`Error parsing JSON from storage for key "${key}":`, e);
      return null;
    }
  }

  /** For compatibility with ngx-webstorage (alias for setItem) */
  store<T>(key: string, value: T): void {
    return this.setItem(key, value);
  }
  setItem<T>(key: string, value: T): void {
    key = normalizeKey(key, this.config.delimiter, this.config.prefix, this.config.suffix, this.config.caseSensitive);
    const oldValue = this.storage.getItem(key);
    let newValue: string | null;
    if (value === undefined || (value === null && this.config.removeNull)) {
      this.storage.removeItem(key);
      newValue = null;
    } else {
      try {
        newValue = JSON.stringify(value);
        this.storage.setItem(key, newValue);
      } catch (e) {
        console.error(`Error setting item in storage for key "${key}":`, e);
      }
    }
    this.internalStorageChanges$.next({ key, newValue, oldValue, storageArea: this.storage });
  }

  removeItem(key: string): void {
    key = normalizeKey(key, this.config.delimiter, this.config.prefix, this.config.suffix, this.config.caseSensitive);
    try {
      const oldValue = this.storage.getItem(key);
      this.storage.removeItem(key);
      this.internalStorageChanges$.next({ key, newValue: null, oldValue, storageArea: this.storage });
    } catch (e) {
      console.error(`Error removing item from storage for key "${key}":`, e);
    }
  }

  /** Allows specification of a key to provide compatibility with ngx-webstorage and also the Web Storage interface */
  clear(key?: string): void {
    if (key) {
      this.removeItem(key);
    } else {
      try {
        this.storage.clear();
      } catch (e) {
        console.error('Error clearing storage:', e);
      }
    }
  }

  key(index: number): string | null {
    try {
      const normalizedKey = this.storage.key(index);
      return denormalizeKey(normalizedKey ?? '', this.config.delimiter, this.config.prefix, this.config.suffix, this.config.caseSensitive);
    } catch (e) {
      console.error(`Error getting key at index ${index}:`, e);
      return null;
    }
  }

  get length(): number {
    try {
      return this.storage.length;
    } catch (e) {
      console.error('Error getting storage length:', e);
      return 0;
    }
  }
}

/** Convenience class for a readable Injection Token */
export class LocalStorageService extends StorageService {
  constructor(config: StorageConfig = DEFAULT_CONFIG) {
    super({ ...config, storage: localStorage });
  }
}
/** Convenience class for a readable Injection Token */
export class SessionStorageService extends StorageService {
  constructor(config: StorageConfig = DEFAULT_CONFIG) {
    super({ ...config, storage: sessionStorage });
  }
}
